import React, { useEffect, useState } from "react";
import api from "../../utils/apis";
import { useAuth } from "../../contexts/AuthContext";

const AddressCard = ({ address, onEdit, onDelete, onSetPrimary }) => {
  const getDisplayType = (type) => {
    const typeMap = {
      home: "HOME",
      work: "WORK",
      other: "OTHER",
    };
    return typeMap[type] || "HOME";
  };

  const displayType = getDisplayType(address.address_type);

  return (
    <div
      className="
        group relative flex h-full flex-col overflow-hidden
        rounded-lg border border-gray-200 bg-white
        p-3
        
      "
    >
      {/* Top Accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-primary" />

      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <span
            data-test="type-badge"
            className="
              inline-flex items-center rounded-full
              bg-primary/10 px-3 py-1.5
              text-[11px] sm:text-xs font-bold
              tracking-[0.15em] text-primary
            "
          >
            {displayType}
          </span>

          {address.is_primary === 1 && (
            <div
              className="
                mt-3 inline-flex items-center gap-1
                rounded-full bg-green-50
                px-3 py-1 text-[11px]
                font-semibold text-green-700
              "
            >
              <span className="h-2 w-2 rounded-full bg-green-500" />
              Primary Address
            </div>
          )}
        </div>
      </div>

      {/* Address Content */}
      <div className="flex-1">
        <p
          data-test="address-text"
          className="
            text-sm leading-7 text-gray-600
          "
        >
          <span className="font-medium text-gray-800">
            {address.street_address}
          </span>

          {address.street_address && ","}

          <br />

          {address.landmark && (
            <>
              {address.landmark},
              <br />
            </>
          )}

          {address.city}, {address.state} - {address.pincode}
          <br />

          <span className="text-gray-500">
            {address.country}
          </span>
        </p>
      </div>

      {/* Divider */}
      <div className="my-2 border-t border-dashed border-gray-200" />

      {/* Actions */}
      <div
        className="
          flex flex-wrap items-center gap-3
          text-sm font-semibold
        "
      >
        <button
          onClick={() => onEdit(address)}
          className="
             bg-primary/10
            px-4 py-2 text-primary
            transition-all duration-200
            hover:bg-primary hover:text-white
          "
        >
          Edit
        </button>

        <button
          onClick={() => onDelete(address.id)}
          className="
             bg-red-50
            px-4 py-2 text-red-600
            transition-all duration-200
            hover:bg-red-500 hover:text-white
          "
        >
          Delete
        </button>

        {address.is_primary !== 1 && (
          <button
            onClick={() => onSetPrimary(address.id)}
            className="
               bg-green-50
              px-4 py-2 text-green-700
              transition-all duration-200
              hover:bg-green-600 hover:text-white
            "
          >
            Set as Primary
          </button>
        )}
      </div>
    </div>
  );
};

const defaultForm = {
  address_type: "home",
  country: "",
  state: "",
  city: "",
  pincode: "",
  streetAddress: "",
  landmark: "",
  isPrimary: false,
};

const Address = () => {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState(defaultForm);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { userData } = useAuth();
  const userId = userData?.id;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  useEffect(() => {
    if (userId) fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchAddresses = async () => {
    setError("");
    setSuccess("");
    try {
      setLoading(true);
      const res = await api.get(`/customer/address/${userId}`);
      setAddresses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching addresses:", err);
      setError("Failed to load addresses");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === "checkbox" ? checked : value;
    if (name === "pincode" && typeof val === "string") {
      val = val.replace(/\D/g, "").slice(0, 6);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  const validateForm = () => {
    if (!formData.country?.trim()) return "Country is required";
    if (!formData.state?.trim()) return "State is required";
    if (!formData.city?.trim()) return "City is required";
    if (!formData.pincode?.trim()) return "Pincode is required";
    if (!/^\d{6}$/.test(formData.pincode)) return "Enter a valid 6-digit PIN code";
    if (!formData.streetAddress?.trim()) return "Street address is required";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const addressPayload = {
      user_id: userId,
      country: formData.country,
      city: formData.city,
      state: formData.state,
      street_address: formData.streetAddress,
      landmark: formData.landmark,
      pincode: formData.pincode,
      is_primary: !!formData.isPrimary,
      address_type: formData.address_type,
    };

    try {
      setLoading(true);
      if (editingAddress) {
        await api.put(`/customer/address/${editingAddress.id}`, addressPayload);
        setSuccess("Address updated successfully");
      } else {
        await api.post("/customer/address", addressPayload);
        setSuccess("Address saved successfully");
      }
      await fetchAddresses();
      resetForm();
    } catch (err) {
      console.error("Error saving address:", err);
      setError("Failed to save address. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (address) => {
    setFormData({
      address_type: address.address_type || "home",
      name: address.name || "",
      country: address.country || "",
      state: address.state || "",
      city: address.city || "",
      pincode: address.pincode || "",
      streetAddress: address.street_address || "",
      landmark: address.landmark || "",
      isPrimary: !!address.is_primary,
    });
    setEditingAddress(address);
    setShowForm(true);
    setError("");
    setSuccess("");
  };

  const handleDelete = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedDeleteId) return;

    setError("");
    setSuccess("");

    try {
      setLoading(true);
      await api.delete(`/customer/address/delete/${selectedDeleteId}`);
      setSuccess("Address deleted");
      await fetchAddresses();
    } catch (err) {
      console.error("Error deleting address:", err);
      setError("Failed to delete address");
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setSelectedDeleteId(null);
    }
  };

  const setAsPrimary = async (id) => {
    setError("");
    setSuccess("");
    const prev = addresses;
    const optimistic = addresses.map((a) => ({
      ...a,
      is_primary: a.id === id ? 1 : 0,
    }));
    setAddresses(optimistic);

    try {
      await api.put(`/customer/address/${id}/set-primary`, { user_id: userId });
      setSuccess("Primary address updated");
      await fetchAddresses();
    } catch (err) {
      console.error("Error setting primary address:", err);
      setAddresses(prev);
      setError("Failed to set primary address");
    }
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingAddress(null);
    setShowForm(false);
    setError("");
    setSuccess("");
  };

  if (loading && addresses.length === 0) {
    return (
      <div className="view-section mt-5 max-w-4xl mx-auto px-4">
        <div className="flex justify-center items-center py-10">
          <div className="text-base sm:text-lg text-gray-600">
            Loading addresses...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="view-section mt-2 p-4">
      <div className="mb-4 flex items-center justify-between gap-3 ">
        <h2 className="md:text-2xl text-xl font-bold text-gray-900">
          {showForm
            ? editingAddress
              ? "Edit Address"
              : "Add New Address"
            : "Saved Addresses"}
        </h2>

        {!showForm && (
          <button
            onClick={() => {
              setShowForm(true);
              setEditingAddress(null);
              setFormData(defaultForm);
              setError("");
              setSuccess("");
            }}
            className="
              text-primary font-semibold text-xs sm:text-sm
              px-3 py-2 rounded-lg
              border border-primary/20
              hover:bg-primary/5 transition-colors
              flex items-center
            "
          >
            <i className="fas fa-plus mr-2" /> Add New
          </button>
        )}
      </div>

      {(error || success) && (
        <div
          className={`mb-4 rounded-md border px-3 py-2 text-sm ${error
            ? "border-red-300 bg-red-50 text-red-700"
            : "border-green-300 bg-green-50 text-green-700"
            }`}
        >
          {error || success}
        </div>
      )}

      {showForm ? (
        <div className="bg-white md:p-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs sm:text-sm font-medium text-gray-700">
                  Address Type
                </label>
                <select
                  name="address_type"
                  value={formData.address_type}
                  onChange={handleInputChange}
                  className="
                    w-full rounded-lg border border-gray-300 px-3 py-2
                    text-sm focus:outline-none
                  "
                >
                  <option value="home">Home</option>
                  <option value="work">Work</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs sm:text-sm font-medium text-gray-700">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="
                    w-full rounded-lg border border-gray-300 px-3 py-2
                    text-sm focus:outline-none 
                  "
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-xs sm:text-sm font-medium text-gray-700">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="
                    w-full rounded-lg border border-gray-300 px-3 py-2
                    text-sm focus:outline-none
                  "
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-xs sm:text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="
                    w-full rounded-lg border border-gray-300 px-3 py-2
                    text-sm focus:outline-none
                  "
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-xs sm:text-sm font-medium text-gray-700">
                  Pincode
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  inputMode="numeric"
                  className="
                    w-full rounded-lg border border-gray-300 px-3 py-2
                    text-sm focus:outline-none 
                  "
                  required
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs sm:text-sm font-medium text-gray-700">
                Street Address
              </label>
              <textarea
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleInputChange}
                rows="3"
                className="
                  w-full rounded-lg border border-gray-300 px-3 py-2
                  text-sm focus:outline-none
                "
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-xs sm:text-sm font-medium text-gray-700">
                Landmark
              </label>
              <input
                type="text"
                name="landmark"
                value={formData.landmark}
                onChange={handleInputChange}
                className="
                  w-full rounded-lg border border-gray-300 px-3 py-2
                  text-sm focus:outline-none 
                "
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isPrimary"
                checked={formData.isPrimary}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50"
              />
              <label className="ml-2 text-xs sm:text-sm text-gray-700">
                Set as Primary Address
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-3">
              <button
                type="submit"
                disabled={loading}
                className="
                  rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-white
                  hover:bg-primary/90 transition-colors disabled:opacity-50
                "
              >
                {loading
                  ? "Saving..."
                  : editingAddress
                    ? "Update Address"
                    : "Save Address"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="
                  rounded-lg border border-gray-300 px-6 py-2 text-sm font-semibold text-gray-700
                  hover:bg-gray-50 transition-colors disabled:opacity-50
                "
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div>
          {addresses.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No addresses</h3>
              <p className="text-gray-600 text-sm mb-6">Add your first address to get started</p>
              <button
                onClick={() => {
                  setShowForm(true);
                  setFormData({ ...defaultForm });
                }}
                className="bg-primary text-white px-6 py-2.5 rounded-lg font-medium hover:bg-primary/80 transition-colors text-sm"
              >
                Add Address
              </button>
            </div>
          ) : (
            <div
              className="
                grid grid-cols-1 gap-4
                sm:grid-cols-2
                lg:grid-cols-3
              "
            >
              {addresses.map((address) => (
                <AddressCard
                  key={address.id}
                  address={address}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onSetPrimary={setAsPrimary}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          />

          {/* Modal */}
          <div className="relative z-10 w-[90%] max-w-sm  bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Delete Address
            </h3>

            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to delete this address?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50"
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Address;
